import React, { useState, useEffect } from 'react';

function SpeedWorkflow() {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('upload'); // 'upload' or 'generate'
  const [sceneCount, setSceneCount] = useState(2); // 2 or 3

  const [formData, setFormData] = useState({
    name: '',
    image: null,
    imagePrompt: '',
    scenePrompt: '',
    aspectRatios: ['9:16'],
    veoFramework: {
      visualStyle: 'realistic',
      cameraMovement: 'static',
      background: '',
      lightingMood: 'natural',
      audioCue: '',
      colorPalette: 'natural',
      disableAudio: false
    }
  });

  const [workflowId, setWorkflowId] = useState(null);
  const [status, setStatus] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [editedScenes, setEditedScenes] = useState({});
  const [saving, setSaving] = useState(false);
  const [creditsEstimate, setCreditsEstimate] = useState(null);

  // Polling per status updates
  useEffect(() => {
    if (!workflowId || !processing) return;

    const pollStatus = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/speed/${workflowId}/status`);
        const data = await response.json();
        setStatus(data);

        if (data.status === 'awaiting_approval') {
          setProcessing(false);
          setStep(2.5); // Scene approval step
        } else if (data.status === 'completed') {
          setProcessing(false);
          setStep(3); // Result step
        } else if (data.status === 'failed') {
          setProcessing(false);
          setError(data.error || 'Workflow failed');
        }
      } catch (err) {
        console.error('Error polling status:', err);
      }
    };

    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [workflowId, processing]);

  // Credits estimation
  useEffect(() => {
    const fetchCreditsEstimate = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/workflows/estimate-credits`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            num_scenes: sceneCount,
            aspect_ratios: formData.aspectRatios
          })
        });
        const data = await response.json();
        setCreditsEstimate(data);
      } catch (err) {
        console.error('Error estimating credits:', err);
      }
    };

    fetchCreditsEstimate();
  }, [sceneCount, formData.aspectRatios]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setFormData({ ...formData, image: file });
      } else {
        alert('Per favore carica solo file immagine (JPG, PNG, GIF)');
      }
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      alert('Inserisci un nome per il workflow');
      return false;
    }
    if (mode === 'upload' && !formData.image) {
      alert('Carica un\'immagine');
      return false;
    }
    if (!formData.imagePrompt.trim()) {
      alert('Inserisci il prompt per l\'immagine');
      return false;
    }
    if (!formData.scenePrompt.trim()) {
      alert('Inserisci il prompt per le scene video');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setProcessing(true);
    setError(null);

    try {
      // Step 1: Create workflow
      const createResponse = await fetch(`${window.location.origin}/api/speed/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          mode: mode,
          image_prompt: formData.imagePrompt,
          scene_count: sceneCount,
          scene_prompt: formData.scenePrompt,
          aspect_ratios: formData.aspectRatios,
          veo_framework: formData.veoFramework
        })
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create workflow');
      }

      const createData = await createResponse.json();
      const wfId = createData.workflow_id;
      setWorkflowId(wfId);

      // Step 2: Upload image if in upload mode
      if (mode === 'upload') {
        const uploadFormData = new FormData();
        uploadFormData.append('file', formData.image);

        const uploadResponse = await fetch(
          `${window.location.origin}/api/speed/${wfId}/upload-image`,
          {
            method: 'POST',
            body: uploadFormData
          }
        );

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }
      }

      // Step 3: Start processing
      const startResponse = await fetch(
        `${window.location.origin}/api/speed/${wfId}/start`,
        {
          method: 'POST'
        }
      );

      if (!startResponse.ok) {
        throw new Error('Failed to start workflow');
      }

      setStep(2); // Progress step
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      setProcessing(false);
    }
  };

  const renderStep1 = () => (
    <div className="speed-form">
      <h2>🚀 Speed Workflow - Generazione Rapida</h2>
      <p className="subtitle">Crea video UGC in pochi minuti senza supervisione manuale</p>

      {/* Workflow Name */}
      <div className="form-group">
        <label>Nome Workflow</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="es. Campagna Prodotto X"
          className="input-field"
        />
      </div>

      {/* Image Mode Selection */}
      <div className="form-group">
        <label>Modalità Immagine</label>
        <div className="mode-selection">
          <label className={`mode-option ${mode === 'upload' ? 'active' : ''}`}>
            <input
              type="radio"
              value="upload"
              checked={mode === 'upload'}
              onChange={(e) => setMode(e.target.value)}
            />
            <span>📤 Upload Immagine</span>
            <small>Carica e modifica con AI</small>
          </label>
          <label className={`mode-option ${mode === 'generate' ? 'active' : ''}`}>
            <input
              type="radio"
              value="generate"
              checked={mode === 'generate'}
              onChange={(e) => setMode(e.target.value)}
            />
            <span>✨ Genera con AI</span>
            <small>Crea da zero con prompt</small>
          </label>
        </div>
      </div>

      {/* Image Upload (if upload mode) */}
      {mode === 'upload' && (
        <div className="form-group">
          <label>Immagine</label>
          <div
            className={`upload-area ${formData.image ? 'has-file' : ''}`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById('imageInput').click()}
          >
            {formData.image ? (
              <div className="uploaded-file">
                <span>✓ {formData.image.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFormData({ ...formData, image: null });
                  }}
                  className="btn-remove"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="upload-prompt">
                <span className="upload-icon">📁</span>
                <p>Clicca o trascina l'immagine qui</p>
                <small>Verrà convertita automaticamente a 9:16 se necessario</small>
              </div>
            )}
          </div>
          <input
            id="imageInput"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Image Prompt */}
      <div className="form-group">
        <label>
          {mode === 'upload' ? 'Prompt Editing Immagine' : 'Prompt Generazione Immagine'}
        </label>
        <textarea
          value={formData.imagePrompt}
          onChange={(e) => setFormData({ ...formData, imagePrompt: e.target.value })}
          placeholder={
            mode === 'upload'
              ? "es. Aggiungi il prodotto in mano alla persona, sfondo luminoso, stile casual"
              : "es. Una persona sorridente che mostra un prodotto tech, sfondo moderno, luce naturale"
          }
          className="textarea-field"
          rows={3}
        />
      </div>

      {/* Scene Count Selection */}
      <div className="form-group">
        <label>Durata Video</label>
        <div className="scene-selection">
          <label className={`scene-option ${sceneCount === 2 ? 'active' : ''}`}>
            <input
              type="radio"
              value={2}
              checked={sceneCount === 2}
              onChange={(e) => setSceneCount(parseInt(e.target.value))}
            />
            <span>2 Scene</span>
            <small>20 secondi totali</small>
          </label>
          <label className={`scene-option ${sceneCount === 3 ? 'active' : ''}`}>
            <input
              type="radio"
              value={3}
              checked={sceneCount === 3}
              onChange={(e) => setSceneCount(parseInt(e.target.value))}
            />
            <span>3 Scene</span>
            <small>30 secondi totali</small>
          </label>
        </div>
      </div>

      {/* Scene Prompt */}
      <div className="form-group">
        <label>Prompt Scene Video</label>
        <textarea
          value={formData.scenePrompt}
          onChange={(e) => setFormData({ ...formData, scenePrompt: e.target.value })}
          placeholder="es. Inizia con hook coinvolgente sul problema, mostra il prodotto come soluzione, termina con CTA chiaro"
          className="textarea-field"
          rows={4}
        />
        <small className="hint">
          💡 Le scene saranno generate in italiano, 10 secondi ciascuna
        </small>
      </div>

      {/* Aspect Ratios Selection */}
      <div className="form-group">
        <label>Formati Video</label>
        <div className="checkbox-group">
          {['9:16', '16:9', '1:1'].map(ratio => (
            <label key={ratio} className="checkbox-option">
              <input
                type="checkbox"
                checked={formData.aspectRatios.includes(ratio)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      aspectRatios: [...formData.aspectRatios, ratio]
                    });
                  } else {
                    setFormData({
                      ...formData,
                      aspectRatios: formData.aspectRatios.filter(r => r !== ratio)
                    });
                  }
                }}
              />
              <span>{ratio} {ratio === '9:16' ? '(Verticale)' : ratio === '16:9' ? '(Orizzontale)' : '(Quadrato)'}</span>
            </label>
          ))}
        </div>
        <small className="hint">
          💡 Seleziona uno o più formati. I video saranno generati in parallelo.
        </small>
      </div>

      {/* VEO Framework Settings */}
      <div className="form-group veo-framework-section">
        <label>
          🎬 VEO Framework - Parametri Avanzati
        </label>
        <p className="section-description">
          Ottimizza la qualità dei video con i parametri del framework VEO 3
        </p>

        <div className="veo-grid">
          {/* Visual Style */}
          <div className="veo-field">
            <label>Stile Visivo</label>
            <select
              value={formData.veoFramework.visualStyle}
              onChange={(e) => setFormData({
                ...formData,
                veoFramework: { ...formData.veoFramework, visualStyle: e.target.value }
              })}
            >
              <option value="realistic">Realistico</option>
              <option value="cinematic">Cinematico</option>
              <option value="documentary">Documentario</option>
              <option value="casual">Casual/UGC</option>
              <option value="professional">Professionale</option>
              <option value="artistic">Artistico</option>
            </select>
          </div>

          {/* Camera Movement */}
          <div className="veo-field">
            <label>Movimento Camera</label>
            <select
              value={formData.veoFramework.cameraMovement}
              onChange={(e) => setFormData({
                ...formData,
                veoFramework: { ...formData.veoFramework, cameraMovement: e.target.value }
              })}
            >
              <option value="static">Statico</option>
              <option value="slow_pan">Pan Lento</option>
              <option value="slow_zoom">Zoom Lento</option>
              <option value="tracking">Tracking</option>
              <option value="handheld">Handheld</option>
              <option value="crane">Crane Shot</option>
              <option value="dutch_angle">Angolo Olandese</option>
            </select>
          </div>

          {/* Lighting/Mood */}
          <div className="veo-field">
            <label>Illuminazione/Mood</label>
            <select
              value={formData.veoFramework.lightingMood}
              onChange={(e) => setFormData({
                ...formData,
                veoFramework: { ...formData.veoFramework, lightingMood: e.target.value }
              })}
            >
              <option value="natural">Naturale</option>
              <option value="soft">Soft/Diffusa</option>
              <option value="dramatic">Drammatico</option>
              <option value="bright">Luminoso</option>
              <option value="moody">Moody/Scuro</option>
              <option value="warm">Caldo</option>
              <option value="cool">Freddo</option>
              <option value="golden_hour">Golden Hour</option>
            </select>
          </div>

          {/* Color Palette */}
          <div className="veo-field">
            <label>Palette Colori</label>
            <select
              value={formData.veoFramework.colorPalette}
              onChange={(e) => setFormData({
                ...formData,
                veoFramework: { ...formData.veoFramework, colorPalette: e.target.value }
              })}
            >
              <option value="natural">Naturale</option>
              <option value="vibrant">Vibrante</option>
              <option value="pastel">Pastello</option>
              <option value="monochrome">Monocromo</option>
              <option value="warm_tones">Toni Caldi</option>
              <option value="cool_tones">Toni Freddi</option>
              <option value="high_contrast">Alto Contrasto</option>
            </select>
          </div>

          {/* Background */}
          <div className="veo-field full-width">
            <label>Background (opzionale)</label>
            <input
              type="text"
              value={formData.veoFramework.background}
              onChange={(e) => setFormData({
                ...formData,
                veoFramework: { ...formData.veoFramework, background: e.target.value }
              })}
              placeholder="es. sfondo bianco minimalista, ambiente urbano, studio setup"
            />
          </div>

          {/* Audio Cue */}
          <div className="veo-field full-width">
            <label>Audio Cue (opzionale)</label>
            <input
              type="text"
              value={formData.veoFramework.audioCue}
              onChange={(e) => setFormData({
                ...formData,
                veoFramework: { ...formData.veoFramework, audioCue: e.target.value }
              })}
              placeholder="es. musica energica, ambiente tranquillo, suoni naturali"
            />
          </div>

          {/* Disable Audio */}
          <div className="veo-field full-width">
            <label className="checkbox-option">
              <input
                type="checkbox"
                checked={formData.veoFramework.disableAudio}
                onChange={(e) => setFormData({
                  ...formData,
                  veoFramework: { ...formData.veoFramework, disableAudio: e.target.checked }
                })}
              />
              <span>Disabilita Audio (aggiunge "(no audio)" al prompt)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Credits Estimate */}
      {creditsEstimate && (
        <div className="credits-estimate-box">
          <h3>💳 Stima Crediti</h3>
          <div className="credits-breakdown">
            <div className="credit-row">
              <span>Scene:</span>
              <strong>{creditsEstimate.scenes}</strong>
            </div>
            <div className="credit-row">
              <span>Formati:</span>
              <strong>{creditsEstimate.formats}</strong>
            </div>
            <div className="credit-row">
              <span>Crediti per video:</span>
              <strong>{creditsEstimate.credits_per_generation}</strong>
            </div>
            <div className="credit-row total">
              <span>Totale Crediti:</span>
              <strong>{creditsEstimate.total_credits}</strong>
            </div>
            <div className="credit-row">
              <span>Costo stimato (USD):</span>
              <strong>${creditsEstimate.estimated_cost_usd}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={processing || formData.aspectRatios.length === 0}
        className="btn-primary btn-large"
      >
        {processing ? '⏳ Creazione...' : '🚀 Genera Video'}
      </button>

      {error && (
        <div className="error-message">
          ❌ Errore: {error}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => {
    const currentStep = status?.current_step || 'processing';
    const stepLabels = {
      'preparing_image': '🎨 Preparazione immagine base...',
      'generating_scenes': '📝 Generazione scene video...',
      'generating_videos': '🎬 Generazione video...',
      'merging_videos': '🔗 Unione video finale...',
      'completed': '✅ Completato!'
    };

    return (
      <div className="speed-progress">
        <h2>Generazione in corso...</h2>
        <p className="workflow-name">Workflow: {status?.name}</p>

        <div className="progress-steps">
          {Object.entries(stepLabels).map(([key, label]) => {
            const isCurrent = currentStep === key;
            const isCompleted = Object.keys(stepLabels).indexOf(currentStep) > Object.keys(stepLabels).indexOf(key);

            return (
              <div
                key={key}
                className={`progress-step ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="step-icon">
                  {isCompleted ? '✓' : isCurrent ? '⏳' : '○'}
                </div>
                <span>{label}</span>
              </div>
            );
          })}
        </div>

        {status?.base_image_url && (
          <div className="preview-section">
            <h3>Immagine Base</h3>
            <img src={status.base_image_url} alt="Base" className="preview-image" />
          </div>
        )}

        {status?.video_scenes && (
          <div className="scenes-section">
            <h3>Scene Generate</h3>
            <div className="scenes-list">
              {Object.entries(status.video_scenes).map(([key, scene]) => (
                <div key={key} className="scene-item">
                  <strong>{key}</strong>
                  <p>{scene.substring(0, 100)}...</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {status?.video_urls && status.video_urls.length > 0 && (
          <div className="videos-section">
            <h3>Video Generati ({status.video_urls.length})</h3>
            <div className="video-grid">
              {status.video_urls.map((url, idx) => (
                <div key={idx} className="video-preview">
                  <video src={url} controls width="200" />
                  <span>Video {idx + 1}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="speed-result">
      <h2>✅ Video Completato!</h2>
      <p className="workflow-name">Workflow: {status?.name}</p>

      <div className="result-video">
        <h3>Video Finali</h3>
        {status?.final_videos ? (
          <div className="multi-format-results">
            {Object.entries(status.final_videos).map(([format, url]) => (
              <div key={format} className="format-result">
                <h4>{format}</h4>
                <video src={url} controls className="final-video" />
                <a href={url} download className="btn-download">
                  📥 Scarica {format}
                </a>
              </div>
            ))}
          </div>
        ) : status?.final_video_url ? (
          <video src={status.final_video_url} controls className="final-video" />
        ) : (
          <p>Caricamento video finale...</p>
        )}
      </div>

      <div className="result-actions">
        <a
          href={status?.final_video_url}
          download
          className="btn-primary"
        >
          📥 Scarica Video
        </a>
        <button
          onClick={() => {
            setStep(1);
            setFormData({
              name: '',
              image: null,
              imagePrompt: '',
              scenePrompt: '',
              aspectRatios: ['9:16'],
              veoFramework: {
                visualStyle: 'realistic',
                cameraMovement: 'static',
                background: '',
                lightingMood: 'natural',
                audioCue: '',
                colorPalette: 'natural',
                disableAudio: false
              }
            });
            setWorkflowId(null);
            setStatus(null);
            setError(null);
          }}
          className="btn-secondary"
        >
          🔄 Nuovo Workflow
        </button>
      </div>

      {status?.video_urls && (
        <div className="result-details">
          <h3>Dettagli</h3>
          <ul>
            <li>Scene generate: {sceneCount}</li>
            <li>Video individuali: {status.video_urls.length}</li>
            <li>Durata totale: {sceneCount * 10} secondi</li>
            <li>Formati: {formData.aspectRatios.join(', ')}</li>
            {status.final_videos && (
              <li>Video finali generati: {Object.keys(status.final_videos).length}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );

  const renderStep2_5 = () => {
    if (!status || !status.video_scenes) return null;

    const scenes = status.video_scenes;

    // Inizializza editedScenes se vuoto
    if (Object.keys(editedScenes).length === 0) {
      setEditedScenes(scenes);
    }

    const handleSceneEdit = (sceneKey, newValue) => {
      setEditedScenes({
        ...editedScenes,
        [sceneKey]: newValue
      });
    };

    const handleSave = async () => {
      setSaving(true);
      try {
        await fetch(`${window.location.origin}/api/speed/${workflowId}/update-scenes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editedScenes)
        });
        alert('Scene salvate!');
      } catch (err) {
        alert('Errore nel salvare le scene');
        console.error(err);
      } finally {
        setSaving(false);
      }
    };

    const handleApprove = async () => {
      setProcessing(true);
      try {
        await fetch(`${window.location.origin}/api/speed/${workflowId}/approve-scenes`, {
          method: 'POST'
        });
        setStep(2); // Torna a progress per monitorare generazione video
      } catch (err) {
        setError('Errore nell\'approvare le scene');
        setProcessing(false);
      }
    };

    return (
      <div className="speed-step">
        <h2>📝 Revisiona Scene Video</h2>
        <p className="step-description">
          Claude ha generato {Object.keys(scenes).length} scene. Puoi modificarle prima di procedere con la generazione video.
        </p>

        <div className="scenes-editor">
          {Object.entries(editedScenes).map(([sceneKey, sceneText], idx) => (
            <div key={sceneKey} className="scene-editor">
              <label>Scena {idx + 1} (8 secondi)</label>
              <textarea
                value={sceneText}
                onChange={(e) => handleSceneEdit(sceneKey, e.target.value)}
                rows={4}
                placeholder="Prompt per la scena video..."
              />
            </div>
          ))}
        </div>

        <div className="scene-actions">
          <button onClick={handleSave} disabled={saving} className="btn-secondary">
            {saving ? 'Salvataggio...' : '💾 Salva Modifiche'}
          </button>
          <button onClick={handleApprove} disabled={processing} className="btn-primary">
            {processing ? 'Avvio...' : '✅ Approva e Genera Video'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="speed-workflow">
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 2.5 && renderStep2_5()}
      {step === 3 && renderStep3()}
    </div>
  );
}

export default SpeedWorkflow;
